import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '../lib/utils.js';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:shadow-[var(--shadow-focus)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Shows a spinner, blocks the click, and sets `aria-busy` so assistive tech
   * announces the state change. Carried over from the design-system button
   * this one replaced — 60 call sites across the apps depend on it, and
   * open-coding a spinner at each would drop the aria-busy every time.
   */
  loading?: boolean;
  /** Rendered before children. The spinner takes its place while loading. */
  leadingIcon?: React.ReactNode;
  /** Rendered after children. Hidden while loading. */
  trailingIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      leadingIcon,
      trailingIcon,
      disabled,
      type,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // A `<button>` with no type is `type="submit"` per HTML, so an untyped
    // Button inside a form fires the primary action on click and on Enter —
    // the ENG-1585 bug. `type` is resolved here, out of `props`, rather than
    // merged in as a default before `{...props}`: an explicit
    // `type={undefined}` — which is how `type={cond ? 'submit' : undefined}`
    // reaches us — is a PRESENT key with an undefined value, so it wins that
    // merge, React emits no attribute, and the submit default is back. Taking
    // `type` out of `props` means spread order no longer decides.
    //
    // Under asChild the default is CHILD-AWARE rather than absent. Slot
    // forwards to whatever the child is, and `<a type>` is a MIME hint, not a
    // button type — meaningless DOM noise on the anchor shape every production
    // call site uses. So the default is supplied only when the Slot child is a
    // direct native `<button>`, which is exactly the shape that would
    // otherwise render no type and submit its form: ENG-1585 again, one level
    // of indirection out. A `type` the child declares itself still wins, since
    // Slot merges child props over slot props.
    //
    // REMAINING LIMIT: `children.type === 'button'` sees the DIRECT child only.
    // `<Button asChild><CustomButton /></Button>`, where CustomButton renders a
    // native `<button>`, still gets no default — `children.type` is a component
    // reference, not the string 'button'. That call site has to supply its own
    // `type` and NOTHING enforces it: no lint rule, no runtime warning, and
    // ButtonProps cannot tell a button-rendering component from an
    // anchor-rendering one. The gap narrows; it does not close.
    // A second limit sits past the check, on Slot's merge:
    // `<Button asChild><button type={undefined}>x</button></Button>` renders no
    // type attribute and submits, and every parent `type` goes down with it —
    // 'button' and 'submit' alike. Slot spreads child props over slot props, so
    // the child's PRESENT-but-undefined key wins whatever Button sends; no prop
    // Button could pass outranks it, the order is fixed inside Slot. Closing it
    // would mean rebuilding the child element to drop the key — `cloneElement`
    // merges, so it cannot delete one — a materially heavier contract than this
    // shape warrants at zero occurrences repo-wide.
    //
    // Forwarding is untouched by the child check, and is NOT the way `disabled`
    // below works: same ternary shape, opposite semantics. `disabled` is
    // SWALLOWED under asChild — an `<a disabled>` does nothing, so the anchor
    // gets `aria-disabled` instead. `type` is FORWARDED for every child kind,
    // anchors included; the check only decides whether to SUPPLY a default,
    // never whether to pass an explicit one through. Swallowing would make
    // `<Button asChild type="button"><button>Cancel</button></Button>` render
    // no type at all and submit the form — ENG-1585 out of a call site that was
    // written correctly.
    //
    // The other unguarded gap, on the non-asChild side: the default inverts
    // the failure mode there. A call site that wants to submit and relies on
    // the old HTML default now renders type="button" and silently submits
    // nothing. Nothing in the repo catches that either — no lint rule, no
    // runtime warning.
    const slotChildIsNativeButton =
      asChild && React.isValidElement(children) && children.type === 'button';
    const resolvedType =
      asChild && !slotChildIsNativeButton ? type : (type ?? 'button');

    // Slot forwards to a single child, so decoration has to go inside it
    // rather than alongside — two children would throw.
    const content = asChild ? (
      children
    ) : (
      <>
        {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : leadingIcon}
        {children}
        {!loading && trailingIcon}
      </>
    );

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={resolvedType}
        disabled={asChild ? undefined : disabled || loading}
        aria-busy={loading || undefined}
        aria-disabled={asChild && (disabled || loading) ? true : undefined}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
